(() => {
  'use strict';

  if (window.__FTRBundleModeGuardInstalled) return;
  window.__FTRBundleModeGuardInstalled = true;

  const PREFIX = '[FTR Bundle Mode Guard]';
  const previousFetch = window.fetch.bind(window);
  const CART_ADD_RE = /\/cart\/add(?:\.js)?(?:\?|$)/;
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const text = (value) => (value == null ? '' : String(value).trim());
  const numericId = (value) => {
    const parsed = Number(String(value ?? '').split('/').pop());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  function normalizePath(value) {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.origin);
      return url.pathname.replace(/\/$/, '') || '/';
    } catch (_) {
      return String(value).split('?')[0].split('#')[0].replace(/\/$/, '');
    }
  }

  function currentBundleIdentity(incomingItems = []) {
    const meta = window.FTRBundleProductMeta || {};
    const wrapper = document.querySelector('[data-loop-custom-bundle]');
    const productForm = document.querySelector('.js-product-form-main');
    const incomingParent = incomingItems.find((item) => {
      const flag = item?.properties?._parent;
      return flag === true || flag === 'true' || flag === 1 || flag === '1';
    });

    const url = normalizePath(meta.url || window.location.pathname);
    const title = text(meta.title);
    const productId = numericId(
      wrapper?.dataset?.productId ||
      productForm?.querySelector('[name="product-id"]')?.value ||
      document.querySelector('[name="product-id"]')?.value
    );
    const parentVariantId = numericId(
      incomingParent?.id ||
      productForm?.querySelector('input[name="id"]')?.value ||
      document.querySelector('.js-product-form-main input[name="id"]')?.value
    );

    return { url, title, productId, parentVariantId };
  }

  function incomingMode(items) {
    if (!Array.isArray(items) || !items.length) return null;

    const subscription = items.some((item) =>
      Boolean(item?.selling_plan || item?.sellingPlan || item?.properties?._bundleId)
    );
    if (subscription) return 'subscription';

    const oneTimeParent = items.some((item) => {
      const flag = item?.properties?._parent;
      return flag === true || flag === 'true' || flag === 1 || flag === '1';
    });
    return oneTimeParent ? 'one-time' : null;
  }

  function cartLineMode(item) {
    if (item?.selling_plan_allocation || item?.selling_plan || item?.properties?._bundleId) {
      return 'subscription';
    }
    return 'one-time';
  }

  function storedBundleUrl(bundleId) {
    if (!bundleId) return '';
    try {
      const direct = sessionStorage.getItem(`ftr-loop-bundle-url:${bundleId}`);
      if (direct) return normalizePath(direct);

      const raw = sessionStorage.getItem(`ftr-loop-bundle-meta:${bundleId}`);
      if (raw) {
        const meta = JSON.parse(raw);
        return normalizePath(meta?.url);
      }
    } catch (_) {}
    return '';
  }

  function subscriptionLineMatches(item, identity) {
    if (cartLineMode(item) !== 'subscription') return false;

    const properties = item?.properties || {};
    const lineUrl = normalizePath(properties._bundleProductUrl);
    const storedUrl = storedBundleUrl(properties._bundleId);
    const lineTitle = text(properties._bundleProductTitle || properties._bundleName);

    if (identity.url && (lineUrl === identity.url || storedUrl === identity.url)) return true;
    if (identity.title && lineTitle && lineTitle === identity.title) return true;
    return false;
  }

  function oneTimeLineMatches(item, identity) {
    if (cartLineMode(item) !== 'one-time') return false;

    const properties = item?.properties || {};
    const lineProductId = numericId(item?.product_id);
    const lineVariantId = numericId(item?.variant_id || item?.id);
    const lineUrl = normalizePath(item?.url || item?.product_url);

    if (identity.productId && lineProductId === identity.productId) return true;
    if (identity.parentVariantId && lineVariantId === identity.parentVariantId) return true;
    if (identity.url && lineUrl === identity.url) return true;

    // Fallback for an untransformed legacy parent line.
    const parentFlag = properties._parent;
    if (
      (parentFlag === true || parentFlag === 'true' || parentFlag === 1 || parentFlag === '1') &&
      identity.parentVariantId && lineVariantId === identity.parentVariantId
    ) {
      return true;
    }

    return false;
  }

  async function readCart() {
    const response = await previousFetch(
      `${window.Shopify?.routes?.root || '/'}cart.js?_ftr_mode=${Date.now()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }
    );

    if (!response.ok) throw new Error(`Unable to inspect cart (${response.status}).`);
    return response.json();
  }

  function collectOppositeLines(cart, mode, identity) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const opposite = mode === 'subscription' ? 'one-time' : 'subscription';

    let matches = items.filter((item) => {
      if (cartLineMode(item) !== opposite) return false;
      return opposite === 'subscription'
        ? subscriptionLineMatches(item, identity)
        : oneTimeLineMatches(item, identity);
    });

    // If we matched one legacy one-time parent, remove all of its legacy children too.
    if (opposite === 'one-time') {
      const sets = new Set(
        matches.map((item) => text(item?.properties?._set)).filter(Boolean)
      );
      if (sets.size) {
        matches = items.filter((item) =>
          matches.includes(item) || sets.has(text(item?.properties?._set))
        );
      }
    }

    // If we matched one Loop child, remove the full transaction group.
    if (opposite === 'subscription') {
      const bundleIds = new Set(
        matches.map((item) => text(item?.properties?._bundleId)).filter(Boolean)
      );
      if (bundleIds.size) {
        matches = items.filter((item) =>
          matches.includes(item) || bundleIds.has(text(item?.properties?._bundleId))
        );
      }
    }

    return matches;
  }

  function cleanedLoopAttributes(cart, removedLines) {
    const removedIds = new Set(
      removedLines.map((item) => text(item?.properties?._bundleId)).filter(Boolean)
    );
    if (!removedIds.size) return null;

    const raw = cart?.attributes?._loopBundleDiscountAttributes;
    if (!raw) return null;

    try {
      const parsed = typeof raw === 'object' ? { ...raw } : JSON.parse(raw);
      removedIds.forEach((id) => delete parsed[id]);
      return JSON.stringify(parsed);
    } catch (_) {
      return null;
    }
  }

  async function removeOppositeMode(mode, incomingItems) {
    const identity = currentBundleIdentity(incomingItems);
    const cart = await readCart();
    const lines = collectOppositeLines(cart, mode, identity);

    if (!lines.length) {
      console.log(PREFIX, 'No opposite-mode copy of this bundle found.', { mode, identity });
      return;
    }

    const updates = {};
    lines.forEach((item) => {
      if (item?.key) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) {
      console.warn(PREFIX, 'Matched opposite bundle lines but found no removable cart keys.', {
        mode,
        identity,
        lines
      });
      return;
    }

    const cleanedAttributes = cleanedLoopAttributes(cart, lines);
    const payload = { updates };
    if (cleanedAttributes != null) {
      payload.attributes = {
        ...(cart.attributes || {}),
        _loopBundleDiscountAttributes: cleanedAttributes
      };
    }

    console.log(PREFIX, 'Replacing opposite purchase mode.', {
      addingMode: mode,
      identity,
      removing: lines.map((item) => ({
        key: item.key,
        title: item.product_title || item.title,
        mode: cartLineMode(item),
        bundleId: item?.properties?._bundleId || null
      })),
      payload
    });

    const response = await previousFetch(
      `${window.Shopify?.routes?.root || '/'}cart/update.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    const result = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(result?.description || result?.message || 'Unable to replace the existing bundle purchase option.');
    }

    console.log(PREFIX, 'Opposite-mode bundle removed successfully.', result);
  }

  window.fetch = async function ftrBundleModeGuardFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(init.method || input?.method || 'GET').toUpperCase();

    if (method === 'POST' && CART_ADD_RE.test(url) && typeof init.body === 'string') {
      try {
        const payload = JSON.parse(init.body);
        const mode = incomingMode(payload?.items);
        if (mode) await removeOppositeMode(mode, payload.items);
      } catch (error) {
        console.error(PREFIX, 'Unable to replace opposite purchase mode.', error);
        throw error;
      }
    }

    return previousFetch(input, init);
  };

  // Subscription verification can race Shopify's cart snapshot. Retry a few
  // times and never turn a successful cart/add response into a false customer error.
  function installSubscriptionValidator() {
    const loop = window.FTRLoopBundle;
    if (!loop) return false;

    loop.validateAddedBundleLines = async function validateAddedBundleLines(expectedItems) {
      const expectedLoopItems = (expectedItems || []).filter((item) => item?.properties?._bundleId);
      if (!expectedLoopItems.length) return [];

      const bundleId = text(expectedLoopItems[0]?.properties?._bundleId);
      const expectedQuantity = expectedLoopItems.reduce(
        (sum, item) => sum + Math.max(0, Number(item?.quantity || 0)),
        0
      );
      const delays = [0, 120, 300, 650, 1100];
      let lastLines = [];

      for (const delay of delays) {
        if (delay) await sleep(delay);
        try {
          const cart = await readCart();
          lastLines = (cart?.items || []).filter(
            (item) => text(item?.properties?._bundleId) === bundleId
          );
          const actualQuantity = lastLines.reduce(
            (sum, item) => sum + Math.max(0, Number(item?.quantity || 0)),
            0
          );

          if (actualQuantity >= expectedQuantity) {
            console.log(PREFIX, 'Subscription bundle verified.', {
              bundleId,
              expectedQuantity,
              actualQuantity,
              lines: lastLines
            });
            return lastLines;
          }
        } catch (error) {
          console.warn(PREFIX, 'Subscription verification retry failed.', error);
        }
      }

      console.warn(PREFIX, 'Subscription add succeeded but delayed cart verification did not reach expected quantity.', {
        bundleId,
        expectedQuantity,
        lines: lastLines
      });
      return lastLines;
    };

    return true;
  }

  if (!installSubscriptionValidator()) {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (installSubscriptionValidator() || attempts >= 40) clearInterval(timer);
    }, 100);
  }

  console.log(PREFIX, 'Active purchase-mode rules:', {
    oneTime: 'Cart Transform parent + children',
    subscription: 'Loop child lines only',
    replacement: 'same bundle + opposite mode is removed before add'
  });
})();
